import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import { VaultHolderPage, listVaultHolders } from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { holdersErrorMessage } from './helpers';

/**
 * `GET /vault/vaults/:id/holders` for one page. Nothing is read without a vault. A response that arrives after the
 * vault or page changed is dropped, so switching vault never shows the previous vault's holders.
 */
export const useVaultHolders = (vaultId: string | null, offset: number, limit: number) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useVaults).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const latest = useRef(0);
  const shownVault = useRef<string | null>(vaultId);
  const [page, setPage] = useState<VaultHolderPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!vaultId) return;
    latest.current += 1;
    const call = latest.current;
    setLoading(true);
    try {
      const result = await listVaultHolders(vaultId, { offset, limit });
      if (call !== latest.current) return;
      setPage(result);
      setLoadError(null);
    } catch (error) {
      if (call !== latest.current) return;
      setPage(null);
      setLoadError(holdersErrorMessage(parseApiError(error), translateRef.current));
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [vaultId, offset, limit]);

  useEffect(() => {
    // A new vault starts clean: the old vault's rows and its error banner must not linger under the new name.
    if (shownVault.current !== vaultId) {
      shownVault.current = vaultId;
      setLoadError(null);
    }
    setPage((current) => (current && current.vaultId === vaultId ? current : null));
    refetch();
  }, [vaultId, refetch]);

  return { page, loading, loadError, refetch };
};
